import type { RecentFixture, FixturesResponse } from '@/types/fixtures';
import { ApiClient } from '@/config/axios.config';

/**
 * RecentFixturesService - handles all API calls related to recent fixtures
 */
export class RecentFixturesService {
  /**
   * Get paginated list of recent fixtures with optional filters
   * Backend API: /fixtures/recent?pageNumber={pageNumber}&pageSize={pageSize}&filters={filters}
   *
   * Filters string format:
   * - "isOnSubs" - filter for fixtures with status = 'Subs' or 'On Subs'
   * - "isOwnFixture" - filter for fixtures where IsOwnFixture = 1
   * - Empty string "" - no filters applied
   */
  static async getRecentFixtures(
    pageNumber = 1,
    pageSize = 50,
    filters?: {
      isOnSubs?: boolean;
      isOwnFixture?: boolean;
      vesselSizeId?: number;
    }
  ): Promise<FixturesResponse> {
    const queryParams = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
    });

    // Build filters string based on backend logic
    // Backend checks: indexOf("isOnSubs") and indexOf("isOwnFixture")
    const filterParts: string[] = [];

    if (filters?.isOnSubs) {
      filterParts.push('isOnSubs');
    }

    if (filters?.isOwnFixture) {
      filterParts.push('isOwnFixture');
    }

    // Join filters with separator (backend uses indexOf, so any separator works)
    const filtersString = filterParts.join(',');
    if (filtersString) {
      queryParams.append('filters', filtersString);
    }

    // Vessel size filter: 0/undefined = All (param omitted), 2 = VLCC, 3 = Suezmax
    if (filters?.vesselSizeId && filters.vesselSizeId !== 0) {
      queryParams.append('vesselSizeId', String(filters.vesselSizeId));
    }

    const data = await ApiClient.get<RecentFixture[]>(`/fixtures/recent?${queryParams}`);

    // Calculate if there are more pages based on returned data
    const hasMore = data.length === pageSize;

    return {
      data,
      totalCount: data.length,
      pageNumber,
      pageSize,
      hasMore,
    };
  }

  /**
   * Get a single recent fixture by ID
   * Backend API: /fixtures/{id}/recent
   */
  static async getRecentFixtureById(fixtureId: number): Promise<RecentFixture> {
    return ApiClient.get<RecentFixture>(`/fixtures/${fixtureId}/recent`);
  }

  /**
   * Get the vessel size ids the current user's company may NOT view (e.g. Suezmax without a
   * Suezmax pool). Used to hide those options from the vessel-size filter. This is UX-only:
   * the recent-fixtures data endpoints hard-gate the same set server-side.
   * Backend API: /fixtures/vessel-size-access
   */
  static async getRestrictedVesselSizes(): Promise<number[]> {
    const response = await ApiClient.get<{ disallowedVesselSizeIds: number[] }>(
      '/fixtures/vessel-size-access'
    );
    return response?.disallowedVesselSizeIds ?? [];
  }

  /**
   * Get recent fixtures with a specific total count (non-paginated)
   * Backend API: /fixtures/recent/{totalCount}
   */
  static async getRecentFixturesByCount(totalCount: number): Promise<RecentFixture[]> {
    return ApiClient.get<RecentFixture[]>(`/fixtures/recent/${totalCount}`);
  }

  /**
   * Refresh recent fixtures data (force fetch from server)
   */
  static async refreshRecentFixtures(filters?: {
    isOnSubs?: boolean;
    isOwnFixture?: boolean;
    vesselSizeId?: number;
  }): Promise<FixturesResponse> {
    return this.getRecentFixtures(1, 50, filters);
  }

  /**
   * Get recent fixture statistics
   */
  static getRecentFixtureStats(fixtures: RecentFixture[]): {
    totalFixtures: number;
    fixedCount: number;
    subsCount: number;
    failedCount: number;
    ownFixtureCount: number;
    scrubberInstalledCount: number;
  } {
    return {
      totalFixtures: fixtures.length,
      fixedCount: fixtures.filter((f) => f.status === 'Fixed').length,
      subsCount: fixtures.filter((f) => f.status === 'Subs' || f.status === 'On Subs').length,
      failedCount: fixtures.filter((f) => f.status === 'Failed').length,
      ownFixtureCount: fixtures.filter((f) => f.isOwnFixture === 1).length,
      scrubberInstalledCount: fixtures.filter((f) => f.scrubberInstalled).length,
    };
  }
}
